// ---------------------------------------------------------------------------
// A total, and where it came from.
//
// One component for every big number in the game, because there used to be two.
// `ScoreRow` drew a total with a `+n` floating above it and the reveal's team
// rows drew their own, with a different chip, a different type scale and a
// different animation. Two implementations of the same idea disagree eventually,
// and the reveal is the one screen where a team total and a player total sit
// four pixels apart.
//
// Three rules the layout depends on:
//
//   1. The number never changes width while it counts. `tabular` fixes the
//      advance of a digit, and the reserved width is the width of the final
//      total, so 98 counting to 102 does not widen the row when it crosses into
//      three digits.
//   2. The `+n` badge keeps its box whether or not it is visible. It fades, it
//      does not unmount, so the number beside it cannot slide sideways as it
//      goes. It is visible first, at the old number, for a beat before anything
//      moves — the badge is what explains the climb, so a badge that only
//      existed after the climb would be explaining nothing.
//   3. The badge sits beside the number, not above it. Floating it over the row
//      above worked while score rows were 90px tall; at the height nine of them
//      have to be on a phone it lands on the previous player's name.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import { useCountUp } from '../../lib/useCountUp.js';

export type ScoreTotalSize = 'sm' | 'md' | 'lg';

const NUMBER_SIZE: Record<ScoreTotalSize, string> = {
  sm: 'text-[26px] sm:text-[32px] lg:text-[34px]',
  md: 'text-[30px] sm:text-[40px] lg:text-[44px]',
  lg: 'text-[36px] sm:text-[52px] lg:text-[62px]',
};

const GAIN_SIZE: Record<ScoreTotalSize, string> = {
  sm: 'text-[14px]',
  md: 'text-[16px]',
  lg: 'text-[20px]',
};

export interface ScoreTotalProps {
  total: number;
  /** What this round added. Omit, or pass zero, when nothing was gained. */
  gain?: number | null;
  size?: ScoreTotalSize;
  /**
   * Perform the climb, rather than arriving at the total.
   *
   * The reveal performs it, and every caller in the game is the reveal, so the
   * default is on. False is for a total that is being *reported* rather than
   * earned — the same number a moment after somebody has already watched it
   * move, where a second climb reads as the score moving twice.
   *
   * That caller was the between-rounds scoreboard, and the scoreboard was
   * removed: the reveal's closing slide is the standing, so a screen after it
   * showed the same leaderboard twice (DESIGN §7). The prop stays because the
   * distinction it draws is real and the next screen wanting a settled total
   * should not have to rediscover it; `DevGallery` keeps a case for it.
   */
  animate?: boolean;
  className?: string;
}

export function ScoreTotal({
  total,
  gain,
  size = 'md',
  animate = true,
  className = '',
}: ScoreTotalProps): ReactElement {
  const shown = typeof gain === 'number' && gain !== 0 ? gain : null;
  const { value, phase } = useCountUp(total - (shown ?? 0), total, animate);
  const explaining = phase === 'holding';

  return (
    <div className={`flex shrink-0 items-baseline gap-2 sm:gap-2.5 ${className}`}>
      {shown === null ? null : (
        <span
          // Hidden from the reading order once the climb has said the same
          // thing: a "+5" still in the accessibility tree beside a settled 12 is
          // a claim that five more are coming.
          aria-hidden={explaining ? undefined : 'true'}
          className={`tabular font-sans leading-none font-bold text-accent transition-opacity duration-500 ease-out ${
            GAIN_SIZE[size]
          } ${explaining ? 'opacity-100' : 'opacity-0'}`}
        >
          +{shown}
        </span>
      )}
      <span
        className={`tabular block text-right font-sans leading-none font-extrabold ${NUMBER_SIZE[size]}`}
        // ch is the advance of a digit under tabular figures, so this reserves
        // exactly the room the final number needs and not a pixel that has to
        // be guessed per breakpoint.
        style={{ letterSpacing: '-0.02em', minWidth: `${String(total).length}ch` }}
      >
        {value}
      </span>
    </div>
  );
}
